import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import IconButton from "@mui/material/IconButton";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import api from "../../../../features/auth/axiosConfig";

interface Props {
  Resheader: string;
  OldOrders?: (orders: any) => void;
  onOrdersUpdated?: () => void;
}
export interface ResheaderRef {
  refreshOrders: () => void;
}

interface Order {
  CustomerEmail: string;
  CustomerName: string;
  CustomerPhoneNo: string;
  Customerid: string;
  GrandTotal: string;
  OrderStatus: string;
  Orderdate: string;
  Orderid: string;
  Ordertime: string;
  Paymentmode: string;
  Products: Array<{
    Amount: string;
    ArabicName: string;
    ProductName: string;
    category_id: string;
    createdBy: string;
    Type: string;
    createdOn: {
      $date: string;
    };
    image_url: string;
    quantity: number;
    _id: {
      $oid: string;
    };
  }>;
  ServiceCharge: number;
  Subtotal: string;
  table_number: string;
  totalAmount: number;
  totalQuantity: number;
  _id: {
    $oid: string;
  };
  total_orders_count: number;
}

interface Pagination {
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

const Resheader = forwardRef<ResheaderRef, Props>((props, ref) => {
  const { Resheader, OldOrders, onOrdersUpdated } = props;
  const [data, setData] = useState<string | null>("");
  const [background, setBackground] = useState<string>("");
  const [fontcolor, setFontColor] = useState<string>("");
  const [profileDropdownVisible, setProfileDropdownVisible] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState<string>("");
  const [currentDate, setCurrentDate] = useState<string>("");
  const [orders, setOrders] = useState<Order[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 0,
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Only fetch orders if we're on the Home page
  const isHomePage = location.pathname === "/v1/Reshome";

  const openEmailCompose = () => {
    const name = data;
    const email = "metanic1234@gmail.com";
    const subject = "Customer Feedback";
    const body = `Business Name: ${name}\nMy Feedback: `;
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, "_blank");
  };

  const fetchOrders = async (pageNumber: number = 1, pageSize: number = 10) => {
    if (!isHomePage) return; // Don't fetch if not on Home page

    try {
      const response = await api.get(`/app/v1/pos/order?pageNumber=${pageNumber}&pageSize=${pageSize}`);
      const pendingOrders = response.data.result.filter((order: Order) => order.OrderStatus === "pending");
      setOrders(pendingOrders);
      setPagination({
        page: response.data.page,
        pageSize: response.data.pageSize,
        totalRecords: response.data.totalRecords,
        totalPages: response.data.totalPages,
      });
      if (typeof OldOrders === "function") {
        OldOrders(pendingOrders);
      }

      if (typeof onOrdersUpdated === "function") {
        onOrdersUpdated();
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  useImperativeHandle(ref, () => ({
    refreshOrders: async () => {
      try {
        await fetchOrders(pagination.page, pagination.pageSize);
        return true;
      } catch (error) {
        console.error("Refresh failed:", error);
        return false;
      }
    },
  }));

  // Only fetch orders when on Home page and when pagination changes
  useEffect(() => {
    if (isHomePage) {
      fetchOrders(pagination.page, pagination.pageSize);
    }
  }, [isHomePage, pagination.page, pagination.pageSize]);

  const handlePageChange = (newPage: number) => {
    if (!isHomePage) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (!isHomePage) return;
    setPagination((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
  };

  const formatDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      month: "long",
      day: "numeric",
      year: "numeric",
    };
    const day = date.getDate();
    let suffix = "th";
    if (day === 1 || day === 21 || day === 31) suffix = "st";
    else if (day === 2 || day === 22) suffix = "nd";
    else if (day === 3 || day === 23) suffix = "rd";

    return `${new Intl.DateTimeFormat("en-US", options).format(date)}${suffix}`;
  };

  const formatTime = (date: Date): string => {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }).format(date);
  };

  useEffect(() => {
    // Initialize user data
    const profileName = sessionStorage.getItem("Profilename");
    setData(profileName);

    const storedColor = sessionStorage.getItem("restaurant_background_color");
    if (storedColor) {
      setBackground(storedColor);
      document.documentElement.style.setProperty("--background-color", storedColor);
    }

    const fontcolor = sessionStorage.getItem("restaurant_font_color");
    if (fontcolor) {
      setFontColor(fontcolor);
      document.documentElement.style.setProperty("--font-color", fontcolor);
    }

    // Set up clock
    const updateDateTime = () => {
      const now = new Date();
      setCurrentTime(formatTime(now));
      setCurrentDate(formatDate(now));
    };

    updateDateTime();
    const intervalId = setInterval(updateDateTime, 1000);

    // Set up click outside handler
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const Logout = () => {
    api.get(`/pos/logout`).then((res) => {
      sessionStorage.removeItem("restaurant_name");
      sessionStorage.removeItem("jsonwebtoken");
      navigate("/");
    });
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownVisible(!profileDropdownVisible);
  };

  const handleOrderSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (!OldOrders) return;

    const orderid = event.target.value;
    const order = orders.find((item) => item.Orderid === orderid);
    OldOrders(order || null);
  };

  return (
    <header className='reshead'>
      <h1 className='head-style'>{Resheader}</h1>
      <div className='date-time-style'>
        <CalendarTodayIcon style={{ marginTop: "3px" }} />
        <p style={{ padding: "5px" }}>
          {currentDate}, {currentTime}
        </p>
      </div>

      {isHomePage && (
        <div className='flex items-center gap-3 text-sm'>
          <span className='font-medium text-gray-700'>Running Orders:</span>

          <select
            className='border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500'
            onChange={handleOrderSelect}
          >
            <option value=''>Select Order</option>
            {orders.map((order) => (
              <option key={order.Orderid} value={order.Orderid}>
                {order.Orderid}
              </option>
            ))}
          </select>

          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className={`px-2 py-1 rounded-md border ${
              pagination.page === 1 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Previous
          </button>

          <span className='text-gray-600'>
            Page {pagination.page} of {pagination.totalPages}
          </span>

          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className={`px-2 py-1 rounded-md border ${
              pagination.page === pagination.totalPages ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Next
          </button>

          <select
            value={pagination.pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className='border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500'
          >
            {[5, 10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </select>
        </div>
      )}

      <div className='header-profiles-restaruent' style={{ marginRight: "20px", width: "auto" }}>
        <div className='header-profile-restaruent' onClick={toggleProfileDropdown}>
          <IconButton>
            <AccountCircleIcon style={{ fontSize: "30px", color: background }} />
            <h4>{data}</h4>
            {profileDropdownVisible ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </IconButton>
          {profileDropdownVisible && (
            <div className='dropdown-content-restaruent' ref={profileDropdownRef}>
              <Link to={"/v1/res-profile"} id='Profile'>
                Profile
              </Link>
              <Link to='' onClick={openEmailCompose} id='feedback'>
                Feedback
              </Link>
              <Link onClick={Logout} to={"/"} style={{ color: "#F14545" }} id='logout'>
                Logout
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
});

export default Resheader;
